import { useState } from 'react'

import classnames from 'classnames'

import type { IconTypes } from '~/components/atoms/Icon/Icon'
import { Icon } from '~/components/atoms/Icon/Icon'
import Text, { TextSizes } from '~/components/atoms/Text/Text'

import styles from './IconTabs.module.css'

export interface TabProps {
  icon: IconTypes
  title: string
  text: string | React.ReactNode
  index?: number
  buttonText?: string
  buttonAction?: () => void
  isActive?: boolean
  setActiveTab?: () => void
  link?: {
    text?: string | null
    url?: string | null
    className?: string | null
  }
}

const Tab = ({
  icon,
  title,
  text,
  buttonText,
  buttonAction,
  isActive,
  setActiveTab,
  index,
}: TabProps) => {
  return (
    <div className={classnames(styles.tab, isActive && styles.tabActive)}>
      <div onClick={setActiveTab}>
        <div className={styles.icon}>
          <Icon type={icon} responsive={true} />
        </div>

        <Text
          Tag="h4"
          size={TextSizes.primaryTitleSml}
          className={styles.tabTitle}
        >
          {title}
        </Text>
      </div>
      <div
        className={classnames(styles.tabContent, styles[`tabContent${index}`])}
      >
        {typeof text === 'string' ? (
          <Text size={TextSizes.bodyMed}>
            <p dangerouslySetInnerHTML={{ __html: text }} className="prose" />
          </Text>
        ) : (
          text
        )}

        {buttonText && (
          <button onClick={buttonAction} className={styles.tabButton}>
            <Text size={TextSizes.primaryTitleXSml}>{buttonText}</Text>
          </button>
        )}
      </div>
    </div>
  )
}

export interface IconTabsProps {
  items: TabProps[]
  testID?: string
}

export default function IconTabs({ items, testID }: IconTabsProps) {
  const [activeTab, setActive] = useState<number>(0)

  return (
    <div className={styles.base} data-testid={testID}>
      {items.map((item, i) => {
        return (
          <Tab
            icon={item.icon}
            title={item.title}
            text={item.text}
            link={item.link}
            key={i}
            setActiveTab={() => setActive(i)}
            isActive={i === activeTab}
            buttonText={item.buttonText}
            buttonAction={item.buttonAction}
            index={i}
          />
        )
      })}
    </div>
  )
}
